<template>
  <div>
    <slot v-if="!listing" name="loading" :slug="props.listingSlug">
      <div class="flex justify-center items-center h-64">
        <div
          class="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-slate-300"
        ></div>
      </div>
    </slot>
    <slot
      v-if="listing"
      name="default"
      :onBid="initiateBid"
      :listing="listing"
      :countdownTo="countdownTo"
      :reloadListing="loadListing"
      :bids="bids"
      :activeBids="activeBids"
      :onCancelBid="cancel.requestCancel"
      :bidError="bidError"
      :cancelError="cancel.cancelError.value"
      :cancelling="cancel.cancelling.value"
      :states="states"
    />
    <slot
      v-if="listing"
      name="bid-modal"
      :confirmBid="confirmBid"
      :dismissBid="dismissBid"
      :cancelBid="dismissBid"
      :listing="listing"
      :copy="dialogCopy"
      :currentBid="currentBid"
    >
      <dialog
        id="bid-dialog"
        class="mx-auto w-full lg:max-w-2xl rounded-lg shadow-lg"
      >
        <h1 class="text-lg font-semibold p-4">{{ dialogCopy.confirmTitle }}</h1>
        <p class="px-4">{{ dialogCopy.confirmIntro }}</p>
        <div class="m-4 grid grid-cols-[3fr,1fr,1fr]">
          <p class="p-2 pl-4 font-semibold bg-boba-sand-light rounded-l-lg">
            Item
          </p>
          <p class="p-2 font-semibold bg-boba-sand-light text-right">
            Quantity
          </p>
          <p
            class="p-2 pr-4 font-semibold text-right bg-boba-sand-light rounded-r-lg"
          >
            {{ dialogCopy.priceHeader }}
          </p>

          <p class="ml-2 p-2 pb-0 md:pb-2 pl-0 col-span-3 md:col-span-1">
            <span v-if="listing.subtitle">{{ listing.subtitle }}: </span>
            <span>{{ listing.title }}</span>
          </p>
          <p class="p-2 pt-0 md:pt-2 col-start-2 text-right">
            {{ currentBid.quantity }}x
          </p>
          <p class="mr-2 p-2 pt-0 md:pt-2 pr-0 text-right">
            {{ $format(currentBid.amount / 1e9) }} miles
          </p>

          <p
            class="ml-2 p-2 col-span-2 text-right font-semibold border-t-2 border-slate-300"
          >
            {{ dialogCopy.totalLabel }}
          </p>
          <p class="mr-2 py-2 text-right border-t-2 border-slate-300">
            {{ $format(totalPrice / 1e9) }} miles
          </p>
        </div>

        <div class="flex justify-end px-4 pb-4">
          <button
            @click="dismissBid"
            class="px-4 py-2 mr-4 border enabled:border-boba-sand rounded-lg enabled:shadow disabled:bg-gray-100 disabled:text-gray-400"
            :disabled="states.bidPosting"
          >
            Cancel
          </button>

          <button
            @click="confirmBid"
            class="enabled:bg-boba-brown enabled:text-white px-4 py-2 rounded-lg enabled:shadow disabled:bg-boba-brown/90 disabled:text-gray-200"
            :disabled="states.bidPosting"
          >
            <span v-if="!states.bidPosting">{{ dialogCopy.confirmButton }}</span>
            <span v-else
              >Processing <i class="fa-solid fa-spinner fa-spin-pulse"
            /></span>
          </button>
        </div>
      </dialog>
    </slot>
    <slot
      v-if="listing"
      name="thanks-dialog"
      :close="closeThanksModal"
      :listing="listing"
      :copy="dialogCopy.thanks"
    >
      <dialog
        id="thanks-dialog"
        class="mx-auto w-full lg:max-w-2xl rounded-lg shadow-lg"
      >
        <h1 class="text-lg font-semibold p-4">{{ dialogCopy.thanks.title }}</h1>
        <p class="px-4">{{ dialogCopy.thanks.body }}</p>

        <div class="mt-4 flex justify-end px-4 pb-4">
          <button
            @click="closeThanksModal"
            class="bg-boba-brown text-white px-4 py-2 rounded-lg shadow"
          >
            Close
          </button>
        </div>
      </dialog>
    </slot>
    <slot
      v-if="listing && listing.isLowBid()"
      name="cancel-dialog"
      :confirmCancel="onConfirmCancel"
      :dismissCancel="cancel.dismissCancel"
      :bid="cancel.pendingBid.value"
      :listing="listing"
      :cancelling="cancel.cancelling.value"
    >
      <cancel-bid-dialog
        id="cancel-dialog"
        :bid="cancel.pendingBid.value"
        :listing="listing"
        :cancelling="cancel.cancelling.value"
        @confirm="onConfirmCancel"
        @dismiss="cancel.dismissCancel"
      />
    </slot>
  </div>
</template>
<script lang="ts" setup>
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
} from "vue";
import { Bid, Errors, Listing, ListingBid } from "@/types";
import { useListingStore } from "@/stores/listing";
import { useAuth } from "@/pkg/auth";
import { DateTime } from "ts-luxon";
import { EventHandler } from "../../util/event-bus";
import { hideModal, showModal } from "@/util/dialog";
import { useCancelBid } from "@/composables/useCancelBid";
import CancelBidDialog from "@/components/Listing/CancelBidDialog.vue";

const authStore = useAuth();
const listingStore = useListingStore();
const props = defineProps<{
  listingSlug: string;
}>();
const emit = defineEmits([
  "confirmBid",
  "confirmedBid",
  "bidSuccessful",
  "bidCancelled",
  "bidCancelFailed",
  "bidDismissed",
  "bidFailed",
  "thanksOpened",
  "thanksClosed",
]);
const currentBid = ref<Bid>({ amount: 0, quantity: 0 });
const listing = ref<Listing | null>(null);
const bidError = ref<Errors.BidError | null>(null);
let countdownTo = ref<DateTime>(DateTime.now());
let listingTimer: any;
let states = reactive({
  confirmingBid: false,
  bidPosting: false,
  bidComplete: false,
  viewingThanks: false,
});

const totalPrice = computed(() => {
  return currentBid.value.amount * currentBid.value.quantity;
});

const bids = computed<ListingBid[]>(() => listing.value?.allBids() ?? []);
const activeBids = computed<ListingBid[]>(
  () => listing.value?.activeBids() ?? [],
);

const cancel = useCancelBid(() => props.listingSlug, {
  dialogId: "cancel-dialog",
  onCancelled: async () => {
    await Promise.all([loadListing(), authStore.fetchBalance()]);
    if (cancel.cancelError.value) {
      emit("bidCancelFailed", cancel.cancelError.value);
    } else {
      emit("bidCancelled");
    }
  },
});

function onConfirmCancel() {
  return cancel.confirmCancel();
}

const dialogCopy = computed(() => {
  if (listing.value?.isLowBid()) {
    return {
      confirmTitle: "Place Bid?",
      confirmIntro: `You're bidding up to ${format(currentBid.value.amount)} miles/ea for ${currentBid.value.quantity} (${format(totalPrice.value)} miles max). Nothing is charged now. If you win, you pay the final clearing price, never more than your max.`,
      confirmButton: "Place Bid",
      priceHeader: "Max Price",
      totalLabel: "Max Total",
      thanks: {
        title: "Bid placed",
        body: "Your bid is in. You can cancel it any time before the auction closes.",
      },
    };
  }

  return {
    confirmTitle: "Complete Purchase?",
    confirmIntro: `${format(totalPrice.value)} miles will be deducted from your account immediately.`,
    confirmButton: "Complete Purchase",
    priceHeader: "Price",
    totalLabel: "Total",
    thanks: {
      title: "Purchase Complete!",
      body: "You will receive a confirmation email with details about your purchase momentarily.",
    },
  };
});

function format(baseUnits: number) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    trailingZeroDisplay: "stripIfInteger",
  } as any).format(baseUnits / 1e9);
}

let eventHandlers: {
  onAuth?: EventHandler;
  onAuthChange?: EventHandler;
} = {};
onBeforeMount(() => {
  loadListing().then(watchListing);
});

onMounted(() => {
  eventHandlers = {
    onAuth: authStore.events.onAuth(() => {
      loadListing();
    }),
    onAuthChange: authStore.events.onAuthChange(() => {
      loadListing();
    }),
  };
});

onBeforeUnmount(() => {
  unwatchListing();
  eventHandlers.onAuth?.off(authStore.events.bus);
  eventHandlers.onAuthChange?.off(authStore.events.bus);
});

/**
 * One request covers everything: the listing endpoint embeds the caller's
 * own bids when authenticated, so a reload after login, a bid, or a cancel
 * refreshes the bid list too.
 */
async function loadListing(): Promise<Listing | null> {
  const l = await listingStore.getListing(props.listingSlug);

  if (!l) {
    return listing.value;
  }

  listing.value = l;
  countdownTo.value = l.countdownTarget() ?? DateTime.now();

  return l;
}

function watchListing(listing: Listing | null) {
  if (listingTimer || !listing) {
    return false;
  }

  let interval = calculateListingRefreshInterval(listing);
  if (interval === null) {
    return false;
  }

  interval = Math.min(interval, MAX_REFRESH_INTERVAL); // this should never be more than 30s;

  listingTimer = setTimeout(() => {
    listingTimer = null;
    loadListing().then(watchListing);
  }, interval * 1000);
}

function unwatchListing() {
  clearTimeout(listingTimer);
  listingTimer = null;
}

const MAX_REFRESH_INTERVAL = 30;
/** Seconds until the next refresh, or null to stop polling. */
function calculateListingRefreshInterval(listing: Listing): number | null {
  if (listing.isSettled()) {
    return null;
  }

  const target = listing.countdownTarget();
  if (!target || (listing.isLowBid() && listing.hasEnded())) {
    return MAX_REFRESH_INTERVAL;
  }

  let diffSeconds = Math.abs(target.diffNow("seconds").as("seconds"));

  if (diffSeconds < 30) {
    return 1;
  } else if (diffSeconds < 120) {
    return 2;
  } else if (diffSeconds < 300) {
    return 7;
  }

  return MAX_REFRESH_INTERVAL;
}

function handleClose() {
  if (states.confirmingBid) {
    states.confirmingBid = false;
  } else if (states.viewingThanks) {
    states.viewingThanks = false;
    closeThanksModal();
  }
}

function initiateBid(bidAmount: number, bidQty: number) {
  bidError.value = null;
  states.confirmingBid = true;
  currentBid.value = { amount: bidAmount, quantity: bidQty };
  showModal("bid-dialog", handleClose);
}

async function confirmBid() {
  if (states.bidPosting) {
    return;
  }

  try {
    states.bidComplete = false;
    states.bidPosting = true;
    emit("confirmedBid");
    await listingStore
      .bid(
        listing.value.slug,
        currentBid.value.amount,
        currentBid.value.quantity,
      )
      .then(() => {
        return Promise.all([loadListing(), authStore.fetchBalance()]);
      });

    states.bidComplete = true;
    states.bidPosting = false;
    emit("bidSuccessful");

    showModal("thanks-dialog", handleClose);
    states.viewingThanks = true;

    emit("thanksOpened");
    hideModal("bid-dialog");
  } catch (error) {
    states.bidPosting = false;
    bidError.value =
      error instanceof Errors.BidError
        ? error
        : new Errors.BidError("An unexpected error occurred");
    hideModal("bid-dialog");
    states.confirmingBid = false;
    emit("bidFailed", bidError.value);

    console.error(error);
  }
}

/** Close the confirmation dialog without placing the bid. */
function dismissBid() {
  states.confirmingBid = false;
  states.bidPosting = false;

  hideModal("bid-dialog");
  emit("bidDismissed");
}

function closeThanksModal() {
  states.viewingThanks = false;
  states.bidComplete = false;

  hideModal("thanks-dialog");
  emit("thanksClosed");
}
</script>
